import { useRef } from "react";
import { ChevronDown, HeartHandshake } from "lucide-react";

interface AboutTabProps {
  isWhyOpen: boolean;
  setIsWhyOpen: (open: boolean) => void;
  isHowOpen: boolean;
  setIsHowOpen: (open: boolean) => void;
  isWhatOpen: boolean;
  setIsWhatOpen: (open: boolean) => void;
  isAccountabilityOpen: boolean;
  setIsAccountabilityOpen: (open: boolean) => void;
  isContactOpen: boolean;
  setIsContactOpen: (open: boolean) => void;
  isDelistedOpen: boolean;
  setIsDelistedOpen: (open: boolean) => void;
  isCommercialRankingsOpen: boolean;
  setIsCommercialRankingsOpen: (open: boolean) => void;
  isMapsDirectoriesOpen: boolean;
  setIsMapsDirectoriesOpen: (open: boolean) => void;
  isTheBeginningOpen: boolean;
  setIsTheBeginningOpen: (open: boolean) => void;
  isDiscoveringMapsOpen: boolean;
  setIsDiscoveringMapsOpen: (open: boolean) => void;
}

export function AboutTab({
  isWhyOpen,
  setIsWhyOpen,
  isHowOpen,
  setIsHowOpen,
  isWhatOpen,
  setIsWhatOpen,
  isAccountabilityOpen,
  setIsAccountabilityOpen,
  isContactOpen,
  setIsContactOpen,
  isDelistedOpen,
  setIsDelistedOpen,
  isCommercialRankingsOpen,
  setIsCommercialRankingsOpen,
  isMapsDirectoriesOpen,
  setIsMapsDirectoriesOpen,
  isTheBeginningOpen,
  setIsTheBeginningOpen,
  isDiscoveringMapsOpen,
  setIsDiscoveringMapsOpen,
}: AboutTabProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const whyRef = useRef<HTMLDivElement>(null);
  const howRef = useRef<HTMLDivElement>(null);
  const whatRef = useRef<HTMLDivElement>(null);
  const accountabilityRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);
  const delistedRef = useRef<HTMLButtonElement>(null);
  const mapsDirectoriesRef = useRef<HTMLButtonElement>(null);
  const commercialRankingsRef = useRef<HTMLButtonElement>(null);
  const theBeginningRef = useRef<HTMLButtonElement>(null);
  const discoveringMapsRef = useRef<HTMLButtonElement>(null);

  // Custom scroll animation function for About tab container
  const scrollToElement = (ref: React.RefObject<HTMLElement>) => {
    if (!ref.current || !scrollContainerRef.current) return;
    
    const element = ref.current;
    const container = scrollContainerRef.current;
    
    // Calculate position using bounding rectangles
    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    
    // Calculate the target scroll position
    const offset = 16; // Small padding from top
    const targetScroll = container.scrollTop + (elementRect.top - containerRect.top) - offset;
    const startScroll = container.scrollTop;
    const distance = targetScroll - startScroll;
    
    // Early return if already at target position
    if (distance === 0) return;
    
    const duration = 300; // 300ms animation
    const startTime = performance.now();
    
    // Easing function for smooth animation
    const easeInOutCubic = (t: number): number => {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    };
    
    // Animation loop
    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeInOutCubic(progress);
      
      container.scrollTop = startScroll + (distance * easedProgress);
      
      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };
    
    requestAnimationFrame(animateScroll);
  };

  return (
    <div ref={scrollContainerRef} className="flex flex-col h-full overflow-y-auto">
      <div className="flex flex-col gap-3 mt-auto">
        <div 
          ref={whyRef} 
          onClick={() => {
            const newState = !isWhyOpen;
            setIsWhyOpen(newState);
            setIsHowOpen(false);
            setIsWhatOpen(false);
            setIsAccountabilityOpen(false);
            setIsContactOpen(false);
            setIsDelistedOpen(false);
            if (!newState) {
              setIsCommercialRankingsOpen(false);
              setIsMapsDirectoriesOpen(false);
            }
            if (newState) {
              setIsTheBeginningOpen(false);
              setIsDiscoveringMapsOpen(false);
              requestAnimationFrame(() => {
                scrollToElement(whyRef);
              });
            }
          }}
          role="button"
          tabIndex={0}
          aria-expanded={isWhyOpen}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              const newState = !isWhyOpen;
              setIsWhyOpen(newState);
              setIsHowOpen(false);
              setIsWhatOpen(false);
              setIsAccountabilityOpen(false);
              setIsContactOpen(false);
              setIsDelistedOpen(false);
              if (!newState) {
                setIsCommercialRankingsOpen(false);
                setIsMapsDirectoriesOpen(false);
              }
              if (newState) {
                setIsTheBeginningOpen(false);
                setIsDiscoveringMapsOpen(false);
                requestAnimationFrame(() => {
                  scrollToElement(whyRef);
                });
              }
            }
          }}
          className={`bg-white/5 border border-white/10 rounded-[15px] px-[6px] py-3 md:px-3 md:py-4 cursor-pointer hover:shadow-[0_0_15px_rgba(var(--accent-blue-rgb),0.3)] mx-auto transition-[width] duration-200 ease-in-out ${
            isWhyOpen 
              ? 'w-full md:w-[640px]' 
              : 'w-[90%] md:w-[640px]'
          }`}
        >
          <div className="w-full flex items-center justify-center relative">
            <h2 className="font-bold text-white text-xl">
              Why
            </h2>
          </div>
          
          {isWhyOpen && (
            <div className="mt-4 space-y-1.5 animate-fade-slide-in">
            
            {/* commercial rankings Sub-container */}
              <button 
                ref={commercialRankingsRef}
                onClick={(e) => {
                  e.stopPropagation();
                  const newState = !isCommercialRankingsOpen;
                  setIsCommercialRankingsOpen(newState);
                  setIsMapsDirectoriesOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    const newState = !isCommercialRankingsOpen;
                    setIsCommercialRankingsOpen(newState);
                    setIsMapsDirectoriesOpen(false);
                  }
                }}
                className={`w-full ${isCommercialRankingsOpen ? 'bg-[#141a28]' : ''} border border-white/10 rounded-[15px] px-2 py-3 cursor-pointer ${!isCommercialRankingsOpen ? 'hover:opacity-80' : ''}`}
                aria-expanded={isCommercialRankingsOpen}
              >
                <div className="w-full flex items-center justify-center relative">
                  <h3 className="font-bold text-white/90 text-lg">
                    no commercial rankings
                  </h3>
                  {!isCommercialRankingsOpen && (
                    <ChevronDown className="absolute right-0 text-white/60" size={20} />
                  )}
                </div>
                
                {isCommercialRankingsOpen && (
                  <div className="mt-3 text-left text-white/90 text-base md:text-lg space-y-3 animate-fade-slide-in">
                    
                   <p>
                              Ranking systems are used to bring content to the top of your search result page. 
                              A ranking system that can be influenced by money, is in my opinion a commercial ranking.
                            </p>
                            
                            <p>
                              And these rankings have a major impact on our decisions: what we buy, read, consume 
                              and where we go in the physical world.
                            </p>
                            <p>
                              However, we do not know anything about these algorithmic systems and how they work. 
                              We have confidence, are comfortable and are happy with terms such as "personalization". 
                              And we all know one signal that we rely on: Ratings and Reviews.
                            </p>
                            
                            <p>
                              These little stars that mean the world in our rating society. But these stars are not 
                              as important for ranking systems as they are for us. There are various other signals 
                              that have more influence on what we see and find online. One signal is 
                              the time and money invested in the discoverability of an offer, product, or physical business.
                            </p>
                            
                            <p>
                              I think the divergence between the way rankings work and our understanding of them is 
                              problematic, especially considering the far-reaching impact of these systems.
                            </p>
                  </div>
                )}
              </button>
              
              {/* Maps & Directories Sub-container */}
              <button 
                ref={mapsDirectoriesRef}
                onClick={(e) => {
                  e.stopPropagation();
                  const newState = !isMapsDirectoriesOpen;
                  setIsMapsDirectoriesOpen(newState);
                  setIsCommercialRankingsOpen(false);
                  if (newState) {
                    requestAnimationFrame(() => {
                      scrollToElement(mapsDirectoriesRef);
                    });
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    const newState = !isMapsDirectoriesOpen;
                    setIsMapsDirectoriesOpen(newState);
                    setIsCommercialRankingsOpen(false);
                    if (newState) {
                      requestAnimationFrame(() => {
                        scrollToElement(mapsDirectoriesRef);
                      });
                    }
                  }
                }}
                className={`w-full ${isMapsDirectoriesOpen ? 'bg-[#141a28]' : ''} border border-white/10 rounded-[15px] px-2 py-3 cursor-pointer ${!isMapsDirectoriesOpen ? 'hover:opacity-80' : ''}`}
                aria-expanded={isMapsDirectoriesOpen}
              >
                <div className="w-full flex items-center justify-center relative">
                  <h3 className="font-bold text-white/90 text-lg">
                    maps & directories
                  </h3>
                  {!isMapsDirectoriesOpen && (
                    <ChevronDown className="absolute right-0 text-white/60" size={20} />
                  )}
                </div>
                
                {isMapsDirectoriesOpen && (
                  <div className="mt-3 text-left text-white/90 text-base md:text-lg space-y-3 animate-fade-slide-in">
                   <p>
                        Since smartphones are part of our daily lives, maps & directories are too.
                      </p>
                      
                      <p>
                        Online maps are used for navigation but also for finding local businesses. Digital business 
                        directories often contain advertising and very dynamic, incomprehensible rankings. Big online 
                        companies that provide these (free) services are shaping the world we live in and they affect 
                        our movement in the physical world.
                      </p>
                      
                      <p>
                        Map and business directory provider Google Maps uses a ranking algorithm to show you a pre-selection 
                        of local search results. Google wants to keep its map experience as clean and easy as possible 
                        by limiting the number of visible entries. The distance to an entry is not the most important 
                        signal for the ranking.
                      </p>  
                      <p>
                        In high-density areas after performing a search, we have to do a maximum 
                        zoom-in and use the "Search this area" option to find a place that might be closer to us, regardless 
                        of the number of good reviews and ratings the entry may have. Google's local ranking mainly depends, among 
                        other "Signals", on the diligent completion of the locations "Google Business Profile" and their 
                        prominence on the world wide web. Quote from Google: "(...) search engine optimization (SEO) best 
                        practices apply."
                      </p>
                      
                      <p>
                        Online search results are subject to critical thinking. Advertising awareness, ranking mechanisms, 
                        data privacy and worth of your own data becoming a part of surfing the daily internet.
                      </p>
                      
                      <p>
                        This idea is an alternativ approach to advertising, non-transparency, the massive 
                        collection of user data, our strange rating society and 'Signals' taking away our job of judgment!
                      </p>
                  </div>
                )}
              </button>
              
            </div>
          )}
        </div>

        {/* How Section */}
        <div 
          ref={howRef} 
          onClick={() => {
            const newState = !isHowOpen;
            setIsHowOpen(newState);
            setIsWhyOpen(false);
            setIsWhatOpen(false);
            setIsAccountabilityOpen(false);
            setIsContactOpen(false);
            setIsDelistedOpen(false);
            
            if (!newState) {
              setIsTheBeginningOpen(false);
              setIsDiscoveringMapsOpen(false);
            }
            
            if (newState) {
              setIsCommercialRankingsOpen(false);
              setIsMapsDirectoriesOpen(false);
              requestAnimationFrame(() => {
                scrollToElement(howRef);
              });
            }
          }}
          role="button"
          tabIndex={0}
          aria-expanded={isHowOpen}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              const newState = !isHowOpen;
              setIsHowOpen(newState);
              setIsWhyOpen(false);
              setIsWhatOpen(false);
              setIsAccountabilityOpen(false);
              setIsContactOpen(false);
              setIsDelistedOpen(false);
              if (!newState) {
                setIsTheBeginningOpen(false);
                setIsDiscoveringMapsOpen(false);
              }
              if (newState) {
                setIsCommercialRankingsOpen(false);
                setIsMapsDirectoriesOpen(false);
                requestAnimationFrame(() => {
                  scrollToElement(howRef);
                });
              }
            }
          }}
          className={`bg-white/5 border border-white/10 rounded-[15px] px-[6px] py-3 md:px-3 md:py-4 cursor-pointer hover:shadow-[0_0_15px_rgba(var(--accent-blue-rgb),0.3)] mx-auto transition-[width] duration-200 ease-in-out ${
            isHowOpen 
              ? 'w-full md:w-[640px]' 
              : 'w-[90%] md:w-[640px]'
          }`}
        >
          <div className="w-full flex items-center justify-center relative">
            <h2 className="font-bold text-white text-xl">
              How
            </h2>
          </div>
          
          {isHowOpen && (
            <div className="mt-4 text-white space-y-1.5 animate-fade-slide-in">
              
              {/* Beginning Sub-container */}
              <button 
                ref={theBeginningRef}
                onClick={(e) => {
                  e.stopPropagation();
                  const newState = !isTheBeginningOpen;
                  setIsTheBeginningOpen(newState);
                  if (newState) {
                    setIsDiscoveringMapsOpen(false);
                    requestAnimationFrame(() => {
                      scrollToElement(theBeginningRef);
                    });
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    const newState = !isTheBeginningOpen;
                    setIsTheBeginningOpen(newState);
                    if (newState) {
                      setIsDiscoveringMapsOpen(false);
                      requestAnimationFrame(() => {
                        scrollToElement(theBeginningRef);
                      });
                    }
                  }
                }}
                className={`w-full ${isTheBeginningOpen ? 'bg-[#141a28]' : ''} border border-white/10 rounded-[15px] px-2 py-3 cursor-pointer ${!isTheBeginningOpen ? 'hover:opacity-80' : ''}`}
                aria-expanded={isTheBeginningOpen}
              >
                <div className="w-full flex items-center justify-center relative">
                  <h3 className="font-bold text-white/90 text-lg">
                    the beginning
                  </h3>
                  {!isTheBeginningOpen && (
                    <ChevronDown className="absolute right-0 text-white/60" size={20} />
                  )}
                </div>
                
                {isTheBeginningOpen && (
                  <div className="mt-3 text-left text-white/90 text-base md:text-lg space-y-3 animate-fade-slide-in">
                  <p>
                             In 2017, I was living in a touristic and busy area in Berlin, Germany, and there were a lot of 
                              bars around. There was no need for Google Maps to find a bar in that neighborhood, but I tried 
                              once and was shocked. The small selection I got from all these bars around me was so bad, and 
                              most were far away. I literally performed that search right in front of my favorite bar, and 
                              Gmaps didn't show it at all. That's when I started to question Google's local ranking mechanism.
                            </p>
                            <p>
                              After realizing how little ratings and reviews influence that ranking, and instead how much bars 
                              can invest in online discoverability, I decided to start my own digital, non-commercial Bar & Pub 
                              map free of rankings, but searchable by information about these places. My main goal with that 
                              project was to raise awareness about Google's local ranking practices.
                            </p>
                            <p>
                              Over the next two years, I wandered through Berlin, visiting bars and pubs to ask the owners 
                              if I could add them to my map and explaining what motivated me. That was a slow process, as I 
                              often found myself drinking a beer and staying too long. It was also often difficult to explain 
                              local ranking algorithms to some older pub owners who didn't even use the internet much. The 
                              strangest thing was that some owners were cautious because there had been others like me running 
                              around offering directory entries, but they just wanted to sell discoverability in the end. Later, 
                              I realized these were the YellowPages sales-army.
                            </p>
                            <p>
                              I added around 80 bars and pubs in that years and learned some html5 to build a website on which 
                              I could embed my map. Unfortunately, the project never caught interest, mainly because I didn't 
                              do any marketing, nor was I able to create a userfriendly website with my code knowledge. But I 
                              was still passionate about understanding ranking mechanics in the online world.
                            </p>
                            <p>
                              Then came the pandemic... Not going to bars anymore was kind of like closing that bar map chapter 
                              for me, as the accuracy of my data was becoming outdated. Maintaining the information for a 
                              directory project is a hell of a difficult task, and depending on the type of data, it often 
                              requires crowdsourced updates.
                            </p>
                            <p>
                              Sitting home alone in 2020, I realized that on my journey over the last three years, I had 
                              discovered a lot of other map projects and many struggle for the same reasons. In a market 
                              segment where there's a monopolistic situation and most people use Google Maps to find things, 
                              there's usually no room for alternative interactive maps.
                            </p>
                            <p>
                              These highly specialized map projects often fly under the radar and are difficult to find. 
                              Also, there are many projects that build maps around things I didn't know even existed. 
                            </p>
                            <p>
                              That's when I first thought about creating a directory for discovering map projects!
                            </p>
                  </div>
                )}
              </button>

              {/* discovering maps Sub-container */}
              <button 
                ref={discoveringMapsRef}
                onClick={(e) => {
                  e.stopPropagation();
                  const newState = !isDiscoveringMapsOpen;
                  setIsDiscoveringMapsOpen(newState);
                  setIsTheBeginningOpen(false);
                  if (newState) {
                    requestAnimationFrame(() => {
                      scrollToElement(discoveringMapsRef);
                    });
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    const newState = !isDiscoveringMapsOpen;
                    setIsDiscoveringMapsOpen(newState);
                    setIsTheBeginningOpen(false);
                    if (newState) {
                      requestAnimationFrame(() => {
                        scrollToElement(discoveringMapsRef);
                      });
                    }
                  }
                }}
                className={`w-full ${isDiscoveringMapsOpen ? 'bg-[#141a28]' : ''} border border-white/10 rounded-[15px] px-2 py-3 cursor-pointer ${!isDiscoveringMapsOpen ? 'hover:opacity-80' : ''}`}
                aria-expanded={isDiscoveringMapsOpen}
              >
                <div className="w-full flex items-center justify-center relative">
                  <h3 className="font-bold text-white/90 text-lg">
                    creating a directory
                  </h3>
                  {!isDiscoveringMapsOpen && (
                    <ChevronDown className="absolute right-0 text-white/60" size={20} />
                  )}
                </div>
                
                {isDiscoveringMapsOpen && (
                  <div className="mt-3 text-left text-white/90 text-base md:text-lg space-y-3 animate-fade-slide-in">
                    <p>
                      Creating a complex category- and location-based visualization system to present map projects was not 
                      an easy task with my limited html5 coding knowledge. However, it did work at a very basic level, allowing 
                      me to launch the first version of the website. It went online in 2020 under the domain 'fava.world', ready 
                      to share some awesome map projects.
                    </p>
                    <p>
                      Even though I discovered UI/UX prototyping tools and fell in love with UX design in general in the years 
                      that followed, the website remained in its initial version. UX design ≠ web development. 
                    </p>
                    <p>
                      In 2025, a design tool I use, launched an AI-powered tool that enables users to turn natural language 
                      prompts into functional web apps. With this tool I was able to publish a complete redesign of the website 
                      by 2026. Prompting my way to version 2 of this website in a few weeks.
                    </p>
                    <p>
                      Instead of plain html5, the site is now built with React & TypeScript. Everything happens client-side 
                      with no external APIs. As long as JavaScript is not disabled in the browser, this setup should work for now. 
                      But it's not professionally written code, and this version is probably only a temporary solution until
                      the website grows. I'm still very happy with this version, as it makes the website way more usable.
                    </p>
                    <p>
                      For this new chapter, I've also moved this project to a more intuitive domain: mapfinder.org 
                    </p>
                  </div>
                )}
              </button>
              
            </div>
          )}
        </div>

        {/* What Section */}
        <div 
          ref={whatRef} 
          onClick={() => {
            const newState = !isWhatOpen;
            setIsWhatOpen(newState);
            setIsWhyOpen(false);
            setIsHowOpen(false);
            setIsAccountabilityOpen(false);
            setIsContactOpen(false);
            setIsDelistedOpen(false);
            
            if (newState) {
              setIsCommercialRankingsOpen(false);
              setIsMapsDirectoriesOpen(false);
              setIsTheBeginningOpen(false);
              setIsDiscoveringMapsOpen(false);
              requestAnimationFrame(() => {
                scrollToElement(whatRef);
              });
            }
          }}
          role="button"
          tabIndex={0}
          aria-expanded={isWhatOpen}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              const newState = !isWhatOpen;
              setIsWhatOpen(newState);
              setIsWhyOpen(false);
              setIsHowOpen(false);
              setIsAccountabilityOpen(false);
              setIsContactOpen(false);
              setIsDelistedOpen(false);
              if (newState) {
                setIsCommercialRankingsOpen(false);
                setIsMapsDirectoriesOpen(false);
                setIsTheBeginningOpen(false);
                setIsDiscoveringMapsOpen(false);
                requestAnimationFrame(() => {
                  scrollToElement(whatRef);
                });
              }
            }
          }}
          className={`bg-white/5 border border-white/10 rounded-[15px] px-[6px] py-3 md:px-3 md:py-4 cursor-pointer hover:shadow-[0_0_15px_rgba(var(--accent-blue-rgb),0.3)] mx-auto ${
            isWhatOpen 
              ? 'w-full md:w-[640px]' 
              : 'w-[90%] md:w-[640px]'
          }`}
        >
          <div className="w-full flex items-center justify-center relative">
            <h2 className="font-bold text-white text-xl">
              What
            </h2>
          </div>
          
          {isWhatOpen && (
            <div className="mt-4 bg-[#141a28] border border-white/10 rounded-[15px] p-3 text-white/90 text-base md:text-lg space-y-3 animate-fade-slide-in-delay">
              <p>
                A discovery platform for interactive maps, to make highly specialized map projects more accessible.
              </p>
              
              <p>
                The build: A privacy-first, responsive single-page application, including filtering, 
                location and category navigation and a search tool.
              </p>
              
              <p>
                All maps are randomly shuffled; there is no ranking on this website.
              </p>
            </div>
          )}
        </div>

        {/* Accountability Section */}
        <div 
          ref={accountabilityRef} 
          onClick={() => {
            const newState = !isAccountabilityOpen;
            setIsAccountabilityOpen(newState);
            setIsWhyOpen(false);
            setIsHowOpen(false);
            setIsWhatOpen(false);
            setIsContactOpen(false);
            setIsDelistedOpen(false);
            
            if (newState) {
              setIsCommercialRankingsOpen(false);
              setIsMapsDirectoriesOpen(false);
              setIsTheBeginningOpen(false);
              setIsDiscoveringMapsOpen(false);
              requestAnimationFrame(() => {
                scrollToElement(accountabilityRef);
              });
            }
          }}
          role="button"
          tabIndex={0}
          aria-expanded={isAccountabilityOpen}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              const newState = !isAccountabilityOpen;
              setIsAccountabilityOpen(newState);
              setIsWhyOpen(false);
              setIsHowOpen(false);
              setIsWhatOpen(false);
              setIsContactOpen(false);
              setIsDelistedOpen(false);
              if (newState) {
                setIsCommercialRankingsOpen(false);
                setIsMapsDirectoriesOpen(false);
                setIsTheBeginningOpen(false);
                setIsDiscoveringMapsOpen(false);
                requestAnimationFrame(() => {
                  scrollToElement(accountabilityRef);
                });
              }
            }
          }}
          className={`bg-white/5 border border-white/10 rounded-[15px] px-[6px] py-2 md:px-3 md:py-2.5 cursor-pointer hover:shadow-[0_0_15px_rgba(var(--accent-blue-rgb),0.3)] mx-auto ${
            isAccountabilityOpen 
              ? 'w-full md:w-[640px]' 
              : 'w-[60%] md:w-[360px]'
          }`}
        >
          <div className="w-full flex items-center justify-center relative">
            <h2 className="font-bold text-white text-lg">
              Accountability
            </h2>
          </div>
          
          {isAccountabilityOpen && (
            <div className="mt-4 bg-[#141a28] border border-white/10 rounded-[15px] p-3 text-white/90 text-base md:text-lg space-y-3 animate-fade-slide-in-delay">
              <p>
                This is a non-commercial website.
              </p>
              <p>
                I try to provide many helpful links to map projects, but I can not take responsibility 
                for their content or availability.
              </p>
              <p>
                There are no ads, cookies, tracking or analyzing build into this site.
              </p>
              <p>
                Server log files: I use Hostinger as my hosting provider. When accessing 
                the website, Hostinger collects data in order to deliver this page.
              </p>
             </div>
          )}
        </div>

        {/* Contact Section */}
        <div 
          ref={contactRef} 
          onClick={() => {
            const newState = !isContactOpen;
            setIsContactOpen(newState);
            setIsWhyOpen(false);
            setIsHowOpen(false);
            setIsWhatOpen(false);
            setIsAccountabilityOpen(false);
            setIsDelistedOpen(false);
            
            if (newState) {
              setIsCommercialRankingsOpen(false);
              setIsMapsDirectoriesOpen(false);
              setIsTheBeginningOpen(false);
              setIsDiscoveringMapsOpen(false);
              requestAnimationFrame(() => {
                scrollToElement(contactRef);
              });
            }
          }}
          role="button"
          tabIndex={0}
          aria-expanded={isContactOpen}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              const newState = !isContactOpen;
              setIsContactOpen(newState);
              setIsWhyOpen(false);
              setIsHowOpen(false);
              setIsWhatOpen(false);
              setIsAccountabilityOpen(false);
              setIsDelistedOpen(false);
              if (newState) {
                setIsCommercialRankingsOpen(false);
                setIsMapsDirectoriesOpen(false);
                setIsTheBeginningOpen(false);
                setIsDiscoveringMapsOpen(false);
                requestAnimationFrame(() => {
                  scrollToElement(contactRef);
                });
              }
            }
          }}
          className={`bg-white/5 border border-white/10 rounded-[15px] px-2 py-2 md:px-3 md:py-2.5 cursor-pointer hover:shadow-[0_0_15px_rgba(var(--accent-blue-rgb),0.3)] mx-auto ${
            isContactOpen 
              ? 'w-full md:w-[640px]' 
              : 'w-[60%] md:w-[360px]'
          }`}
        >
          <div className="w-full flex items-center justify-center relative">
            <h2 className="font-bold text-white text-lg">
              Contact
            </h2>
          </div>
          
          {isContactOpen && (
            <div className="mt-4 bg-[#141a28] border border-white/10 rounded-[15px] p-3 text-white/90 text-base md:text-lg space-y-3 animate-fade-slide-in-delay">
              <p>
               If you have map recommendations, ideas about the project or critical thoughts, I'm happy about every feedback:
              </p>
              <p className="text-center text-base md:text-lg">
              <strong>hello@mapfinder.org</strong>
              </p>
            </div>
          )}
        </div>

        {/* Delisted Section */}
        <div className="flex flex-col items-center gap-3">
          <button
            ref={delistedRef}
            onClick={(e) => {
              e.stopPropagation();
              const newState = !isDelistedOpen;
              setIsDelistedOpen(newState);
              if (newState) {
                setIsWhyOpen(false);
                setIsHowOpen(false);
                setIsWhatOpen(false);
                setIsAccountabilityOpen(false);
                setIsContactOpen(false);
                setIsCommercialRankingsOpen(false);
                setIsMapsDirectoriesOpen(false);
                setIsTheBeginningOpen(false);
                setIsDiscoveringMapsOpen(false);
                requestAnimationFrame(() => {
                  scrollToElement(delistedRef);
                });
              }
            }}
            className="flex items-center justify-center gap-2 text-white/70 hover:text-white cursor-pointer mx-auto"
          >
          <HeartHandshake className="w-5 h-5" />
        </button>
          
          {isDelistedOpen && (
            <div 
              onClick={() => setIsDelistedOpen(false)}
              className="mt-3 text-white/80 space-y-2 animate-fade-slide-in text-sm md:text-base cursor-pointer pb-4 max-w-xs"
            >
              <p className="text-center font-bold text-white/90 mb-3">In tribute to the delisted ones:</p>
              <ul className="space-y-1.5 pl-4">

                <li>• arriving-in-berlin</li>
                <li>• berlin-playgrounds</li>
                <li>• berlinmusicmap</li>
                <li>• berliner-hundeauslaufgebiete</li>
                <li>• billard-map</li>
                <li>• find-the-masks</li>
                <li>• history-maps</li>
                <li>• hundewiese-hamburg</li>
                <li>• jedermannsland</li>
                <li>• kicker-map</li>
                <li>• lpg-stations-of-sweden</li>
                <li>• mobile-box</li>
                <li>• open-lavs</li>
                <li>• ourplymouth</li>
                <li>• pumpsofberlin</li>
                <li>• spotthestation-nasa</li>
                <li>• the-north-coast-500</li>
                <li>• wasserwandern-in-berlin</li>
                <li>• winnipeg-map-for-newcomers</li>
                
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}